import React, { useState } from "react";
import { Box, Divider, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryField, QueryInfo } from "system/query/QueryInfo";
import { QueryFieldSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import TitleCase from "components/formatting/TitleCase";
import SettingsLegend from "components/settingsConfig/uiComponents/SettingsLegend";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import { chordChartDefaultSettings, ChordChartSettingsStateType } from "./ChordChartUtils";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";

const defaultWidgetRequest = {
  name: 'chord-chart',
  endpoint: 'query/agg',
  queryParams: {
    include_total: true
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

function BuildRequestFromFieldId(fieldId: string): WidgetRequest {
  const widgetRequest: WidgetRequest = {
    ...defaultWidgetRequest,
    requestInit: {
      body: {
        aggs: {
          "outer": {
            "op": "keyword",
            "field": fieldId,
            "aggs": {
              "chords": {
                "op": "keyword",
                "field": fieldId
              }
            }
          }
        }
      }
    }
  };
  return widgetRequest;
}

export function BuildDefaultChordChartRequests(queryInfo: QueryInfo): WidgetRequest[] | undefined {
  const foundKeywordsFields = queryInfo.fields?.filter((field:QueryField) => {
    return field.type === "keywords" && field.agg_supported;
  }) || false;

  if (foundKeywordsFields && foundKeywordsFields?.length) {
    // Try to use emotions if available, otherwise default to the first keywords field
    const foundKeywordField = foundKeywordsFields.find((field:QueryField) => {
      return field.id === "emotions";
    }) || foundKeywordsFields[0];
    return [BuildRequestFromFieldId(foundKeywordField.id)];
  }
}

export default function ChordChartSettingsContent(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'chord-chart');
  const requestBody = originalRequest?.requestInit?.body || {};
  const fieldId = requestBody?.aggs?.outer?.field || "";

  const [selectedFieldId, setSelectedFieldId] = useState<string>(fieldId);
  const [settingsState, setSettingsState] = useState<ChordChartSettingsStateType>({...chordChartDefaultSettings, ...props?.widget?.data?.settings});
  const settingsOnChange = (updatedState: ChordChartSettingsStateType) => setSettingsState(updatedState);

  const [tabValue, setTabValue] = useState<number>(0);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  function updateWidget() {
    const widgetRequest = BuildRequestFromFieldId(selectedFieldId);
    props.saveWidgetDataAndRequests({settings: settingsState}, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }

  const colorOptions = [{
    label: i18n.t("widget-types.chord-chart.settings.color-by-source"),
    value: "source"
  }, {
    label: i18n.t("widget-types.chord-chart.settings.color-by-target"),
    value: "target"
  }];

  const chordChartImage = settingsState.colorBy === "target" ? "chord-chart-target.png" : "chord-chart-source.png";

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widget-settings.query-and-data"), i18n.t("display")]}
      title={i18n.t("widget-types.chord-chart.settings.title")}>
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
            <QueryFieldSelector
              allowedFieldTypes={["keywords"]}
              requireAggSupported={true}
              selectedFieldId={selectedFieldId}
              setSelectedFieldId={(newFieldId: string) => {
                setSelectedFieldId(newFieldId);
              }}
              fields={props.queryInfo.fields} />
          </>)}
        </>)}

        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Widget Graph Preview */}
            <Stack
              sx={{ flex: 1, textAlign: "center", width: isSmallScreen ? "100%" : "50%" }}>
              {settingsState.widgetTitle !== 'none' &&
                <Typography
                  sx={{ fontSize: "1.2rem", textAlign: settingsState.widgetTitleAlignment }}
                  variant="subtitle2">
                  <TitleCase text={settingsState.widgetTitle} />
                </Typography>}
                <Box sx={{
                  textAlign: "center"
                }}>
                  <img src={`/img/widgets/${chordChartImage}`} width="225" alt="Chord Chart Image" />
                </Box>
            </Stack>

            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")}>
                {/* Widget Title */}
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                  {settingsState.widgetTitle === 'custom' &&
                    <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                      <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                      <SettingsTextField
                        keyid="customWidgetTitle"
                        label={i18n.t("widget-settings.custom-text")}
                        value={settingsState.customWidgetTitle} />
                    </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsAccordion label={i18n.t("widget-types.chord-chart.settings.chart-settings")}>
                {/* Chart Settings */}
                <SettingsSingleSelect
                  defaultValue={colorOptions.find((item: OptionsValueType) => (
                    item.value === settingsState.colorBy
                  )) ?? colorOptions[0]}
                  keyid="colorBy"
                  label={i18n.t("widget-types.chord-chart.settings.color-by")}
                  options={colorOptions}
                />
              </SettingsAccordion>
              <SettingsLegend
                settingsState={settingsState}
              />
            </SettingsOptions>
          </SplitSettingsWrapper>}
      </>
    </TabbedDialog>
  );
}
